import React from 'react'
import {
	User,
	Mail,
	Phone,
	Building,
	Shield,
	Calendar,
	Clock,
	UserCheck,
	UserX,
	Key,
	Edit2,
	X,
} from 'lucide-react'
import { Modal, Button } from '@components/common'
import './UserView.css'

/**
 * UserView Component
 *
 * Read-only view of user details with organized sections
 *
 * Features:
 * - Comprehensive user information display
 * - Formatted values (dates, phone)
 * - Status badges
 * - Avatar with initials
 * - Quick edit and action buttons
 * - Accessible and responsive
 *
 * @param {Object} userData - User data to display
 * @param {Function} onClose - Close modal callback
 * @param {Function} onEdit - Edit action callback (optional)
 */
const UserView = ({ userData, onClose, onEdit }) => {
	if (!userData) return null

	/**
	 * Format date
	 */
	const formatDate = (dateString) => {
		if (!dateString) return '-'
		return new Date(dateString).toLocaleDateString('en-US', {
			year: 'numeric',
			month: 'long',
			day: 'numeric',
		})
	}

	/**
	 * Format date with time
	 */
	const formatDateTime = (dateString) => {
		if (!dateString) return 'Never'
		return new Date(dateString).toLocaleString('en-US', {
			year: 'numeric',
			month: 'long',
			day: 'numeric',
			hour: '2-digit',
			minute: '2-digit',
		})
	}

	/**
	 * Get user initials
	 */
	const getInitials = () => {
		const firstName = userData.first_name || ''
		const lastName = userData.last_name || ''
		return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase() || 'U'
	}

	/**
	 * Get full name
	 */
	const getFullName = () => {
		const firstName = userData.first_name || ''
		const lastName = userData.last_name || ''
		return `${firstName} ${lastName}`.trim() || 'No Name'
	}

	/**
	 * Get status badge class
	 */
	const getStatusClass = (status) => {
		switch (status?.toLowerCase()) {
			case 'active':
				return 'user-view__badge--success'
			case 'inactive':
				return 'user-view__badge--secondary'
			case 'blocked':
				return 'user-view__badge--danger'
			default:
				return 'user-view__badge--secondary'
		}
	}

	/**
	 * Get role badge class
	 */
	const getRoleClass = () => {
		return 'user-view__badge--primary'
	}

	const isBlocked = userData.status?.toLowerCase() === 'blocked'
	const isActive = userData.status?.toLowerCase() === 'active'

	const footer = (
		<>
			{onEdit && (
				<Button variant="primary" onClick={onEdit}>
					<Edit2 size={18} />
					<span>Edit User</span>
				</Button>
			)}
			<Button variant="secondary" onClick={onClose}>
				Close
			</Button>
		</>
	)

	return (
		<Modal isOpen={true} onClose={onClose} title="User Details" size="large" footer={footer}>
			<div className="user-view">
				{/* Header Section */}
				<div className="user-view__header">
					<div className="user-view__header-content">
						<div className="user-view__avatar">
							{userData.avatar_url ? (
								<img src={userData.avatar_url} alt={getFullName()} className="user-view__avatar-img" />
							) : (
								<div className="user-view__avatar-initials">
									<User size={32} />
								</div>
							)}
						</div>
						<div>
							<h2 className="user-view__title">{getFullName()}</h2>
							<p className="user-view__email">
								<Mail size={16} />
								{userData.email}
							</p>
						</div>
					</div>
					<div className={`user-view__badge ${getStatusClass(userData.status)}`}>
						{userData.status || 'Unknown'}
					</div>
				</div>

				{/* User Details */}
				<div className="user-view__section">
					<h3 className="user-view__section-title">User Information</h3>
					<div className="user-view__grid">
						<div className="user-view__item">
							<div className="user-view__item-icon">
								<Building size={20} />
							</div>
							<div className="user-view__item-content">
								<span className="user-view__item-label">Company</span>
								<span className="user-view__item-value">{userData.company_name || 'Not assigned'}</span>
							</div>
						</div>

						<div className="user-view__item">
							<div className="user-view__item-icon">
								<Shield size={20} />
							</div>
							<div className="user-view__item-content">
								<span className="user-view__item-label">Role</span>
								<span className="user-view__item-value">
									<span className={`user-view__badge ${getRoleClass()}`}>
										{userData.role_name || 'No Role'}
									</span>
								</span>
							</div>
						</div>

						<div className="user-view__item">
							<div className="user-view__item-icon">
								<Phone size={20} />
							</div>
							<div className="user-view__item-content">
								<span className="user-view__item-label">Phone</span>
								<span className="user-view__item-value">{userData.phone || 'Not provided'}</span>
							</div>
						</div>

						<div className="user-view__item">
							<div className="user-view__item-icon">
								<UserCheck size={20} />
							</div>
							<div className="user-view__item-content">
								<span className="user-view__item-label">Email Verified</span>
								<span className="user-view__item-value">
									{userData.email_verified_at ? 'Yes' : 'Not verified'}
								</span>
							</div>
						</div>

						<div className="user-view__item">
							<div className="user-view__item-icon">
								<Clock size={20} />
							</div>
							<div className="user-view__item-content">
								<span className="user-view__item-label">Last Login</span>
								<span className="user-view__item-value">
									{userData.last_login_at ? formatDateTime(userData.last_login_at) : 'Never'}
								</span>
							</div>
						</div>

						<div className="user-view__item">
							<div className="user-view__item-icon">
								<Calendar size={20} />
							</div>
							<div className="user-view__item-content">
								<span className="user-view__item-label">Member Since</span>
								<span className="user-view__item-value">{formatDate(userData.created_at)}</span>
							</div>
						</div>
					</div>
				</div>

				{/* Metadata */}
				<div className="user-view__section">
					<h3 className="user-view__section-title">Metadata</h3>
					<div className="user-view__metadata">
						<div className="user-view__metadata-item">
							<span className="user-view__metadata-label">Created</span>
							<span className="user-view__metadata-value">{formatDate(userData.created_at)}</span>
						</div>
						<div className="user-view__metadata-item">
							<span className="user-view__metadata-label">Last Updated</span>
							<span className="user-view__metadata-value">{formatDate(userData.updated_at)}</span>
						</div>
						{userData.deleted_at && (
							<div className="user-view__metadata-item">
								<span className="user-view__metadata-label">Deleted</span>
								<span className="user-view__metadata-value">{formatDate(userData.deleted_at)}</span>
							</div>
						)}
					</div>
				</div>
			</div>
		</Modal>
	)
}

export default UserView
